import React from 'react'

import type { ExportBackgroundActionRequestPayload } from '@services/types'

import { cancellablePromise, parseApiErrorMessage } from '@helpers'

import { useAlertDispatch } from '@contexts/AlertProvider'

import { useCancellablePromiseRef } from '@hooks'

import { DownloadIcon } from '@oldComponents/ui/SvgIcons'

import { AsyncButtonWithIcon } from './Buttons'

import { ExportButtonMessage } from '@messages'

interface BaseBulkExportActionButtonProps {
  callStartBulkExport: AsyncFunction<
    Pick<ExportBackgroundActionRequestPayload, 'setInfoAlert'>,
    BackgroundActionResponse
  >
  callStopBulkExport: VoidFunction
}

export function BaseBulkExportActionButton({
  callStartBulkExport,
  callStopBulkExport,
}: BaseBulkExportActionButtonProps) {
  const [loading, setLoading] = React.useState(false)
  const { setErrorAlert, setInfoAlert } = useAlertDispatch()
  const cPromiseRef = useCancellablePromiseRef<BackgroundActionResponse>()
  const stopRef = React.useRef(callStopBulkExport)

  React.useEffect(() => {
    const callStopAction = stopRef.current
    return () => {
      if (cPromiseRef.current) {
        callStopAction()
      }
    }
  }, [cPromiseRef])

  async function handleClick() {
    setLoading(true)
    try {
      cPromiseRef.current = cancellablePromise(callStartBulkExport({ setInfoAlert }))
      await cPromiseRef.current.promise

      setLoading(false)
    } catch (error) {
      const errorMessage = parseApiErrorMessage(error)
      if (errorMessage) {
        setErrorAlert(errorMessage)
        setLoading(false)
      }
    }
  }

  return (
    <AsyncButtonWithIcon icon={<DownloadIcon />} loading={loading} onClick={handleClick} variant="primaryContained">
      {ExportButtonMessage}
    </AsyncButtonWithIcon>
  )
}
